import { CertificateReward } from '@yandex-int/k-common/typings';
import { DocumentFilled, Like } from 'education-icons';
import qs from 'query-string';
import React, { useCallback } from 'react';
import { IntlShape } from 'react-intl';
import { useHistory } from 'react-router';
import ym from 'react-yandex-metrika';

import { LessonInformationBanner } from 'common.components/lesson-view.information-banner/information-banner';
import {
    InformationBanner,
    InformationBanners,
} from 'common.components/lesson-view.information-banners/information-banners';

import './lesson-view__information-banners.scss';

interface Props {
    certificate: CertificateReward | null;
    copyLessonAsRNO: ({}) => void;
    clessonId: number | null;
    feedbackUrl: string;
    intl: IntlShape;
    lessonTitle: string;
    shouldShowRNOBanner: boolean;
    shouldShowFeedbackBanner: boolean;
    teacherId: number | null;
}

enum BANNER_TYPE {
    FEEDBACK = 'feedback',
    CERTIFICATE = 'certificate',
    RNO = 'rno',
}

const defaultBannersPriorityMap = {
    [BANNER_TYPE.CERTIFICATE]: 0,
    [BANNER_TYPE.RNO]: 1,
    [BANNER_TYPE.FEEDBACK]: 2,
};

export const LessonViewInformationBanners = (props: Props) => {
    const {
        certificate,
        clessonId,
        copyLessonAsRNO,
        feedbackUrl,
        intl,
        lessonTitle,
        shouldShowRNOBanner,
        shouldShowFeedbackBanner,
        teacherId,
    } = props;

    const history = useHistory();

    const handleCertificateClick = useCallback(() => {
        if (certificate) {
            ym('reachGoal', 'teacher_lesson_statistics_diagnostics_report_open', {
                teacher_id: teacherId,
                clesson_id: clessonId,
            });

            window.open(certificate.url, '_blank');
        }
    }, [certificate, clessonId, teacherId]);

    const handleFeedbackClick = useCallback(() => {
        if (feedbackUrl) {
            ym('reachGoal', 'teacher_lesson_feedback_button_click', {
                teacher_id: teacherId,
                clesson_id: clessonId,
            });

            const parsedUrl = qs.parseUrl(feedbackUrl);
            const fullFeedbacklUrl = `${parsedUrl.url}?${qs.stringify({
                ...parsedUrl.query,
                clessonId,
                teacherId,
            })}`;

            window.open(fullFeedbacklUrl, '_blank', 'noopener');
        }
    }, [feedbackUrl, clessonId, teacherId]);

    const handleRNOClick = useCallback(() => {
        if (!clessonId) {
            return;
        }

        copyLessonAsRNO({
            sourceId: clessonId,
            newName: intl.formatMessage({ id: 'lesson.copyNewName.RNO' }, { name: lessonTitle }),
            intl,
            history,
        });
    }, [copyLessonAsRNO, intl, history, clessonId, lessonTitle]);

    const banners: InformationBanner<BANNER_TYPE>[] = [];

    if (certificate) {
        banners.push({
            element: (
                <LessonInformationBanner
                    action={handleCertificateClick}
                    buttonTextId={`lesson.informationBanner.certificate.${certificate.slug}.buttonText`}
                    icon={<DocumentFilled className="lesson-view__information-banner-icon-document" />}
                    key={`${BANNER_TYPE.CERTIFICATE}.${certificate.slug}`}
                    mix="lesson-view__information-banner"
                    textId={`lesson.informationBanner.certificate.${certificate.slug}.text`}
                />
            ),
            type: BANNER_TYPE.CERTIFICATE,
        });
    }

    if (shouldShowFeedbackBanner) {
        banners.push({
            element: (
                <LessonInformationBanner
                    action={handleFeedbackClick}
                    buttonTextId={'lesson.informationBanner.teacherFeedback.buttonText'}
                    icon={<Like className="lesson-view__information-banner-icon-feedback" />}
                    key={BANNER_TYPE.FEEDBACK}
                    mix="lesson-view__information-banner"
                    textId={'lesson.informationBanner.teacherFeedback.text'}
                />
            ),
            type: BANNER_TYPE.FEEDBACK,
        });
    }

    if (shouldShowRNOBanner) {
        banners.push({
            element: (
                <LessonInformationBanner
                    action={handleRNOClick}
                    buttonTextId={'lesson.informationBanner.errorCorrection.buttonText'}
                    icon={<DocumentFilled className="lesson-view__information-banner-icon-document" />}
                    key={BANNER_TYPE.RNO}
                    mix="lesson-view__information-banner qa_lesson-rno-banner"
                    textId={'lesson.informationBanner.errorCorrection.text'}
                />
            ),
            type: BANNER_TYPE.RNO,
        });
    }

    return (
        <InformationBanners
            banners={banners}
            mix="lesson-view__information-banners"
            priorityMap={defaultBannersPriorityMap}
        />
    );
};
